/**
 * Did the second speaker add KNOWLEDGE, or only words?
 *
 * WHY THIS EXISTS. In a debate, convergence is a bug and `echoScore` catches
 * it. In a collaboration, agreement is the GOAL, so echo alone can no longer
 * tell success from failure: two speakers landing on the same conclusion might
 * be the point, or might be one voice talking to itself twice.
 *
 * The discriminator is not in the prose. Every turn already records which
 * lattice rows its retrieval touched, so the question becomes arithmetic:
 *
 *   collaboration is WORKING when the union of retrieved rows grows
 *   collaboration is FAILING when a speaker adds prose over rows already seen
 *
 * Same weights and same topic on both sides, so the only thing that can grow
 * that union is the second lattice genuinely contributing something the first
 * did not have. That is a cleaner success signal than anything the debate mode
 * has, and it costs nothing to collect because `rowsTouched` was already there.
 *
 * A CAUTION THAT MATTERS. Rows are counted PER SPEAKER, because row 41 of the
 * gardening pack and row 41 of the hermes pack are unrelated facts. Pooling
 * them by number would report two speakers reading entirely different things as
 * perfect overlap. The key is speaker plus row, always.
 */

export type CoverageTurn = {
  /** Which lattice this turn read from. */
  speakerKey: string;
  /** Rows that lattice's retrieval touched this turn. */
  rowsTouched: number[];
};

export type CoverageStep = {
  speakerKey: string;
  /** Rows this turn touched that no earlier turn had. */
  fresh: number;
  /** Rows it touched that were already on the table. */
  repeat: number;
  /** Size of the union after this turn. */
  total: number;
};

export type Coverage = {
  steps: CoverageStep[];
  /** Distinct (speaker, row) pairs across the whole exchange. */
  union: number;
  /**
   * Fraction of turns that brought at least one new row.
   *
   * The headline. A collaboration where this falls toward zero is one where the
   * speakers have run out of memory and are elaborating rather than
   * contributing, which is the moment to stop rather than to add exchanges.
   */
  contributingTurns: number;
  /** Per speaker, how many rows it alone put on the table. */
  bySpeaker: Record<string, number>;
};

const key = (speakerKey: string, row: number) => `${speakerKey}#${row}`;

export function coverage(turns: CoverageTurn[]): Coverage {
  const seen = new Set<string>();
  const steps: CoverageStep[] = [];
  const bySpeaker: Record<string, number> = {};

  for (const t of turns) {
    let fresh = 0;
    let repeat = 0;
    // De-duplicate WITHIN the turn first: retrieval can legitimately return the
    // same row twice across a think pass and an answer pass, and counting that
    // as two contributions would flatter every turn equally.
    for (const row of new Set(t.rowsTouched)) {
      const k = key(t.speakerKey, row);
      if (seen.has(k)) repeat++;
      else {
        seen.add(k);
        fresh++;
        bySpeaker[t.speakerKey] = (bySpeaker[t.speakerKey] ?? 0) + 1;
      }
    }
    steps.push({ speakerKey: t.speakerKey, fresh, repeat, total: seen.size });
  }

  const contributing = steps.filter((s) => s.fresh > 0).length;
  return {
    steps,
    union: seen.size,
    // Zero turns is 0, not 1. An empty exchange contributed nothing, and
    // reporting it as perfect contribution would be the worst possible default
    // for a metric whose whole job is to say when to stop.
    contributingTurns: steps.length === 0 ? 0 : contributing / steps.length,
    bySpeaker,
  };
}
